// Manager-side leave queue (nursing-ops-2 #02/#03), hosted on Staff Roster:
// pending requests to approve/reject, and approved leave that can be revoked
// (recall from leave). Approving flips overlapping roster rows to `leave`
// server-side and hard-blocks the range; revoking lifts the block but leaves
// flipped rows for the manager to re-set by hand — both by ratified decision.
//
// Approving also runs a client-side staffing check (2026-07-26): if the
// nurse is rostered today and removing them would leave a ward
// Stretched/Over-limit/Unstaffed, a confirm dialog shows the impact before
// the approval is sent. Warn-only — the manager can still proceed. See
// docs/superpowers/specs/2026-07-26-leave-vs-ratio-check-design.md.
import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid,
  LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import { NURSE_COLORS } from "../theme";
import { getLeaveRequests, actOnLeave, getWardRoster, listOf } from "../api/nurseApi";
import { computeLeaveImpact } from "../wardRatio";

const STATUS_TEXT = {
  adequate: "Adequate",
  stretched: "Stretched",
  over: "Over limit",
  unstaffed: "No nurse would be rostered",
};

const fmtRatio = (r) => (r == null ? "no roster" : `1 : ${r.toFixed(1)}`);

const toStr = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function LeaveApprovalsSection({ onChanged, beds = [], nurseNameById = {} }) {
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [todayRoster, setTodayRoster] = useState([]);
  const [impactDialog, setImpactDialog] = useState(null); // { row, impacts }

  const reload = useCallback(() => {
    setLoading(true);
    Promise.all([
      getLeaveRequests({ status: "pending" }),
      getLeaveRequests({ status: "approved" }),
    ])
      .then(([p, a]) => { setPending(listOf(p)); setApproved(listOf(a)); })
      .catch(() => { setPending([]); setApproved([]); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  // Today's ward roster, for the approve-impact check. Independent of the
  // leave-request fetch above; failure degrades to "no impact data" (see
  // handleApproveClick) rather than blocking anything.
  useEffect(() => {
    const today = toStr(new Date());
    getWardRoster({ from_date: today, to_date: today })
      .then((r) => setTodayRoster(listOf(r)))
      .catch(() => setTodayRoster([]));
  }, []);

  const act = async (row, action) => {
    setBusyId(row.id);
    try {
      const res = await actOnLeave(row.id, action);
      if (res?.status !== "Success") {
        toast.error(res?.message || `Could not ${action}`);
      } else {
        toast.success(res?.message || `Leave ${action}d`);
        reload();
        onChanged?.();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || `Could not ${action}`);
    } finally {
      setBusyId(null);
    }
  };

  // Gate on the ratio impact before approving. Empty `beds` or `todayRoster`
  // (still loading, or the fetch failed) means computeLeaveImpact has
  // nothing to work with and returns [] — approval proceeds exactly as it
  // does today. The check can only ever add a warning, never a new failure
  // mode.
  const handleApproveClick = (row) => {
    const impacts = computeLeaveImpact(row.nurse_id, beds, todayRoster);
    if (impacts.length === 0) {
      act(row, "approve");
    } else {
      setImpactDialog({ row, impacts });
    }
  };

  const confirmApprove = () => {
    const row = impactDialog?.row;
    setImpactDialog(null);
    if (row) act(row, "approve");
  };

  const renderTable = (rows, kind) => (
    <Paper sx={{ borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}`, overflowX: "auto", mb: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
            <TableCell>Nurse</TableCell><TableCell>From</TableCell><TableCell>To</TableCell><TableCell>Reason</TableCell>
            {kind === "approved" && <TableCell>Rows flipped</TableCell>}
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} hover>
              <TableCell>{r.nurse_name || nurseNameById[r.nurse_id] || r.nurse_id}</TableCell>
              <TableCell>{r.from_date}</TableCell>
              <TableCell>{r.to_date}</TableCell>
              <TableCell sx={{ maxWidth: 280 }}>
                <Typography variant="body2" noWrap title={r.reason}>{r.reason}</Typography>
              </TableCell>
              {kind === "approved" && <TableCell>{r.roster_rows_flipped ?? "—"}</TableCell>}
              <TableCell align="right">
                {kind === "pending" ? (
                  <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
                    <Button size="small" variant="contained" color="success" disabled={busyId === r.id} onClick={() => handleApproveClick(r)}>
                      Approve
                    </Button>
                    <Button size="small" color="error" variant="text" disabled={busyId === r.id} onClick={() => act(r, "reject")}>
                      Reject
                    </Button>
                  </Stack>
                ) : (
                  <Button size="small" color="warning" variant="outlined" disabled={busyId === r.id} onClick={() => act(r, "revoke")}>
                    Revoke
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
          {!loading && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={kind === "approved" ? 6 : 5} align="center">
                <Typography variant="body2" color="text.secondary" sx={{ py: 2.5 }}>
                  {kind === "pending" ? "No pending leave requests." : "No approved leave on the books."}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );

  return (
    <Box sx={{ mb: 3 }}>
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, textTransform: "uppercase", letterSpacing: 0.5, fontSize: 11 }}>
        Ward Roster
      </Typography>
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={12} md={6}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
            <Typography variant="subtitle2">Pending Leave Requests</Typography>
            {pending.length > 0 && <Chip size="small" color="warning" label={pending.length} />}
          </Stack>
          {renderTable(pending, "pending")}
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack direction="row" spacing={0.75} sx={{ alignItems: "baseline", mb: 1 }}>
            <Typography variant="subtitle2">Approved Leave</Typography>
            <Typography variant="caption" color="text.secondary">(revocable)</Typography>
          </Stack>
          {renderTable(approved, "approved")}
        </Grid>
      </Grid>

      <Dialog open={Boolean(impactDialog)} onClose={() => setImpactDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Approving will affect staffing</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {impactDialog?.row?.nurse_name || nurseNameById[impactDialog?.row?.nurse_id] || impactDialog?.row?.nurse_id} is rostered today.
            Approving flips their row(s) to Leave, which would leave:
          </Typography>
          <Stack spacing={1.5}>
            {impactDialog?.impacts.map((imp, i) => (
              <Box key={i}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {imp.wardName} · {imp.shift}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {imp.occupied} patient(s) · {fmtRatio(imp.before.ratio)} → {fmtRatio(imp.after.ratio)} · {STATUS_TEXT[imp.after.statusKey]}
                </Typography>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImpactDialog(null)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={confirmApprove}>
            Approve Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
